import React, { useCallback, useEffect, useState } from 'react';
import {
  VStack,
  Heading,
  Card,
  CardHeader,
  CardBody,
  Button,
  useToast,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Image,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { ExcelTeamUpload } from '../../components/mundial/ExcelTeamUpload';
import { ExcelScheduleUpload } from '../../components/mundial/ExcelScheduleUpload';
import { AssignMatchTeams } from '../../components/admin/AssignMatchTeams';
import { ResponsiveTableContainer } from '../../components/ui/ResponsiveTableContainer';
import { mundialTeamService } from '../../services/mundial/mundialTeamService';
import { seedGroupMatchesForTeams } from '../../services/mundial/mundialMatchService';
import { mundialBracketService } from '../../services/mundial/mundialBracketService';
import { MundialTeam } from '../../types/mundial';

const GROUP_STAGE_START = new Date('2026-06-11T12:00:00');

export const AdminMundialPage: React.FC = () => {
  const [teams, setTeams] = useState<MundialTeam[]>([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const loadTeams = useCallback(async () => {
    const data = await mundialTeamService.getAllTeams();
    setTeams(data);
  }, []);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const handleSeedMatches = async () => {
    if (teams.length < 48) {
      toast({
        title: 'Faltan selecciones',
        description: `Hay ${teams.length} equipos. Se recomiendan 48 (12 grupos × 4).`,
        status: 'warning',
        duration: 5000,
      });
    }
    setLoading(true);
    try {
      const groupCount = await seedGroupMatchesForTeams(teams, GROUP_STAGE_START);
      const knockoutCount = await mundialBracketService.seedKnockoutPlaceholders(
        GROUP_STAGE_START
      );
      toast({
        title: 'Calendario generado',
        description: `${groupCount} partidos de grupos y ${knockoutCount} de eliminatorias`,
        status: 'success',
        duration: 5000,
      });
    } catch (e) {
      toast({
        title: 'Error',
        description: e instanceof Error ? e.message : 'No se pudo generar el calendario',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePopulateBracket = async () => {
    setLoading(true);
    try {
      const result = await mundialBracketService.populateRoundOf32FromGroups();
      if (result.skipped) {
        toast({
          title: 'Fase de grupos incompleta',
          description: 'Captura todos los resultados antes de armar las llaves',
          status: 'warning',
          duration: 4000,
        });
      } else {
        toast({
          title: 'Llaves actualizadas',
          description: `${result.updated} cruces de 16vos definidos`,
          status: 'success',
          duration: 4000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      <Heading size="lg">Administración — Mundial FIFA 2026</Heading>

      <Card>
        <CardHeader>
          <Heading size="md">Cargar selecciones desde Excel</Heading>
        </CardHeader>
        <CardBody>
          <ExcelTeamUpload onUploadComplete={loadTeams} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <Heading size="md">Calendario y llaves (Excel)</Heading>
        </CardHeader>
        <CardBody>
          <ExcelScheduleUpload onUploadComplete={loadTeams} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <Heading size="md">Asignar equipos por match_id</Heading>
        </CardHeader>
        <CardBody>
          <AssignMatchTeams />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <Heading size="md">Herramientas adicionales</Heading>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={3}>
            <Text fontSize="sm" color="gray.600">
              Si no tienes Excel de calendario, puedes generar uno automático (fechas
              aproximadas). Los resultados se capturan en Administración → Resultados.
            </Text>
            <Button
              variant="outline"
              onClick={handleSeedMatches}
              isLoading={loading}
              isDisabled={teams.length < 4}
            >
              Generar calendario automático (sin Excel)
            </Button>
            <Button variant="outline" onClick={handlePopulateBracket} isLoading={loading}>
              Recalcular clasificados en 16vos
            </Button>
          </VStack>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <Heading size="md">Selecciones registradas ({teams.length})</Heading>
        </CardHeader>
        <CardBody>
          {teams.length === 0 ? (
            <Alert status="info">
              <AlertIcon />
              Importa un Excel o ejecuta <code>npm run seed:mundial</code>
            </Alert>
          ) : (
            <ResponsiveTableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Grupo</Th>
                  <Th>Bandera</Th>
                  <Th>Nombre</Th>
                  <Th>Código</Th>
                  <Th>Confederación</Th>
                </Tr>
              </Thead>
              <Tbody>
                {teams.map((t) => (
                  <Tr key={t.id}>
                    <Td>{t.group}</Td>
                    <Td>
                      <Image src={t.flag_url} alt={t.name} boxSize="28px" />
                    </Td>
                    <Td>{t.name}</Td>
                    <Td>{t.code}</Td>
                    <Td>{t.confederation || '—'}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            </ResponsiveTableContainer>
          )}
        </CardBody>
      </Card>

      {loading && (
        <Spinner />
      )}
    </VStack>
  );
};
